import { memo, useEffect, useRef, useState } from 'react';
import { Box, Grid, Typography, CircularProgress } from '@material-ui/core';
import { useFormik } from 'formik';
import InputText from '../../../components/InputText';
import { FleetsService } from '../../../services';
import { IFilterVariables, IVehicleFilters } from '../../../services/models';
import { formatMoney } from '../../../utils/formatters/formatMoney';
import { useCars } from '../hooks/useCars';
import CarBrandIcon from './CarBrandIcon';
import useStyles, { StyledSlider } from './styles';

const CarFilter = memo(() => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const { setCars } = useCars();

  let debounceTimeoutRef = useRef<any>();

  const [defaultVariables, setDefaultVariables] = useState<
    Partial<IFilterVariables>
  >({
    maxPrice: 0,
    minPrice: 0,
    maxKM: 0,
    minKM: 0,
    brands: [],
  });

  const formik = useFormik<IVehicleFilters>({
    initialValues: {
      startPrice: defaultVariables?.minPrice,
      endPrice: defaultVariables?.maxPrice,
      startKm: defaultVariables?.minKM,
      endKm: defaultVariables?.maxKM,
      startYear: 0,
      endYear: 0,
    },
    enableReinitialize: true,
    onSubmit(values) {},
  });

  useEffect(() => {
    (async () => {
      setLoading((prevState) => !prevState);
      const { data } = await FleetsService.filterVariables();
      setDefaultVariables(data);
      setLoading((prevState) => !prevState);
    })();
  }, []);

  useEffect(() => {
    debounceTimeoutRef.current = setTimeout(async () => {
      if (!Object.is(formik.initialValues, formik.values)) {
        const { data } = await FleetsService.list(formik.values);
        setCars(data as any);
      }
    }, 500);
    return () => {
      clearTimeout(debounceTimeoutRef.current);
    };
  }, [formik.values, setCars, formik.initialValues]);

  const handlePriceChange = (_: any, newValue: any) => {
    const [startPrice, endPrice] = newValue;
    formik.setFieldValue('startPrice', startPrice);
    formik.setFieldValue('endPrice', endPrice);
  };

  const handleKmChange = (_: any, newValue: any) => {
    const [startKm, endKm] = newValue;
    formik.setFieldValue('startKm', startKm);
    formik.setFieldValue('endKm', endKm);
  };

  const handleBrandChange = (value: string) => {
    formik.setFieldValue('brand', value, false);
  };

  const handleClearFilters = (e: any) => {
    e.preventDefault();
    formik.setValues({
      startPrice: defaultVariables?.minPrice,
      endPrice: defaultVariables?.maxPrice,
      startKm: defaultVariables?.minKM,
      endKm: defaultVariables?.maxKM,
      startYear: 0,
      endYear: 0,
    });
  };

  return (
    <>
      <Box className={classes.filters}>
        <form>
          <Typography className={classes.filterTitle}>filtros</Typography>
          <Box display="flex" flexDirection="column">
            <Box className={classes.brandsFilter}>
              <Typography className={classes.filterTitle}>Marcas</Typography>
              {loading ? (
                <Box
                  className={classes.brandsFilter}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <Grid
                  container
                  justify="space-between"
                  className={classes.brandsBox}
                  spacing={2}
                >
                  {defaultVariables?.brands?.map((item) => (
                    <Grid key={item} item xs={6} sm={6}>
                      <CarBrandIcon
                        name="brand"
                        value={item}
                        label={item}
                        active={item === formik.getFieldMeta('brand').value}
                        onClick={handleBrandChange}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
            <Box>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                marginTop={1}
              >
                <Typography className={classes.filterTitle}>Preço</Typography>
                <Box display="flex" justifyContent="center">
                  <StyledSlider
                    value={[
                      formik.getFieldMeta('startPrice').value,
                      formik.getFieldMeta('endPrice').value,
                    ]}
                    onChange={handlePriceChange}
                    min={defaultVariables.minPrice}
                    max={defaultVariables.maxPrice}
                    step={500}
                  />
                </Box>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography style={{ fontSize: '10px' }} color="initial">
                  {formatMoney(defaultVariables.minPrice as number)}
                </Typography>
                <Typography style={{ fontSize: '10px' }} color="initial">
                  {formatMoney(defaultVariables.maxPrice as number)}
                </Typography>
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                marginTop={1}
              >
                <Typography className={classes.filterTitle}>Ano</Typography>
                <Box
                  display="flex"
                  justifyContent="space-around"
                  alignItems="center"
                >
                  <InputText
                    classes={{ root: classes.filterInput }}
                    placeholder="2017"
                    name="startYear"
                    onChange={formik.handleChange}
                  ></InputText>
                  <Typography>Até</Typography>
                  <InputText
                    classes={{ root: classes.filterInput }}
                    placeholder="2023"
                    name="endYear"
                    onChange={formik.handleChange}
                  ></InputText>
                </Box>
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                marginTop={1}
              >
                <Typography className={classes.filterTitle}>KM</Typography>
                <Box display="flex" justifyContent="center">
                  <StyledSlider
                    value={[
                      formik.getFieldMeta('startKm').value,
                      formik.getFieldMeta('endKm').value,
                    ]}
                    onChange={handleKmChange}
                    min={defaultVariables.minKM}
                    max={defaultVariables.maxKM}
                    step={500}
                  />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography style={{ fontSize: '10px' }} color="initial">
                    {defaultVariables.minKM} KM
                  </Typography>
                  <Typography style={{ fontSize: '10px' }} color="initial">
                    {defaultVariables.maxKM} KM
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="center" marginTop="20px">
                  <button
                    className={classes.buttonFilters}
                    onClick={handleClearFilters}
                  >
                    Limpar Filtros
                  </button>
                </Box>
              </Box>
            </Box>
          </Box>
        </form>
      </Box>
    </>
  );
});
export default CarFilter;
